import { useEffect, useMemo, useRef } from 'react';

/**
 * Mounts a portal root on document.body, locks page scroll, and restores focus.
 */
export const useModalPortal = ({
    rootClassName,
    bodyLockClass,
    portalMarker,
}) => {
    const portalEl = useMemo(() => {
        const div = document.createElement('div');

        div.classList.add(rootClassName);
        div.setAttribute('data-modal-portal', portalMarker);

        return div;
    }, [rootClassName, portalMarker]);

    const dialogRef = useRef(null);
    const lastActiveElRef = useRef(null);

    useEffect(() => {
        if (
            typeof document === 'undefined' ||
            window.__PUPPETEER_PREBUILD_MODE__
        )
            return;

        const activePortals = Array.from(
            document.body.querySelectorAll('[data-modal-portal]')
        );

        activePortals.forEach((node) => {
            if (node !== portalEl && node.parentNode) {
                node.parentNode.removeChild(node);
            }
        });

        Array.from(document.body.classList).forEach((className) => {
            if (className.endsWith('-modal-is-open')) {
                document.body.classList.remove(className);
            }
        });

        document.body.appendChild(portalEl);
        document.body.classList.add(bodyLockClass);

        lastActiveElRef.current = document.activeElement;
        queueMicrotask(() => dialogRef.current?.focus());

        return () => {
            if (portalEl.parentNode) {
                portalEl.parentNode.removeChild(portalEl);
            }

            document.body.classList.remove(bodyLockClass);

            const lastActiveElement = lastActiveElRef.current;

            if (
                lastActiveElement &&
                typeof lastActiveElement.focus === 'function'
            ) {
                lastActiveElement.focus();
            }
        };
    }, [portalEl, bodyLockClass]);

    return { portalEl, dialogRef };
};
